import Component from "@glimmer/component";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { tracked } from "@glimmer/tracking";
import { htmlSafe } from "@ember/template";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { helper } from "@ember/component/helper";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";
import WazeSemiCategoryDialogContent from "../../components/waze-semi-category-dialog-content";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import {
  WAZE_SEMI_CATEGORIES,
  WAZE_SEMI_CATEGORY_OTHER_ID,
} from "../../lib/waze-semi-categories";

const getNormalizedSemiCategories = (relativeUrlRoot = "/") =>
  WAZE_SEMI_CATEGORIES.reduce((acc, semiCategory) => {
    acc[semiCategory.id] = {
      ...semiCategory,
      name: i18n(`waze.semi_categories.names.${semiCategory.id}`),
      categories: [],
      image: `${relativeUrlRoot}${semiCategory.image}`,
    };
    return acc;
  }, {});

const styleForSemiCategoryLogo = helper(([semiCategory]) => {
  const styleStringComponents = [];
  if (semiCategory?.color) {
    styleStringComponents.push(`background-color: #${semiCategory.color}`);
  }
  return htmlSafe(`${styleStringComponents.join(";")}`);
});

export default class WazeSemiCategories extends Component {
  @service siteSettings;
  @tracked dialogSemiCategory = {};
  dialogElement = null;

  // The `category` object is always present for a category page, but it's `undefined` for the main page
  // where this component is rendered as well.
  get category() {
    return this.args.outletArgs.category;
  }

  get subcategories() {
    return this.category?.subcategories || [];
  }

  get shouldShowSemiCategories() {
    return (
      this.subcategories.length > 0 &&
      this.category?.custom_fields?.waze_show_semi_category_layout
    );
  }

  get normalizedSemiCategories() {
    const normalizedSemiCategories = getNormalizedSemiCategories(
      this.siteSettings.waze_semi_category_relative_url_root,
    );

    this.subcategories.forEach((category) => {
      const semiCategoryId = category.custom_fields?.waze_semi_category_id;
      const targetId = normalizedSemiCategories[semiCategoryId]
        ? semiCategoryId
        : WAZE_SEMI_CATEGORY_OTHER_ID;

      normalizedSemiCategories[targetId].categories.push(category);
    });

    return normalizedSemiCategories;
  }

  get availableSemiCategories() {
    return Object.values(this.normalizedSemiCategories).filter(
      (semiCategory) => semiCategory.categories.length > 0,
    );
  }

  @action
  registerDialog(element) {
    this.dialogElement = element;
  }

  @action
  onSemiCategoryClick(semiCategory) {
    this.dialogSemiCategory = semiCategory;
    this.dialogElement?.showDialog();
  }

  @action
  onDialogClose() {
    this.dialogElement?.hideDialog();
  }

  <template>
    {{#if this.shouldShowSemiCategories}}
      <div class="waze-semi-categories">
        <wz-h7>{{i18n "waze.semi_categories.list.search_community"}}</wz-h7>
        <div class="waze-semi-categories-list">
          {{#each this.availableSemiCategories as |c|}}
            <div
              class="waze-semi-category"
              {{on "click" (fn this.onSemiCategoryClick c)}}
            >
              <div
                class="waze-semi-category-logo"
                style={{styleForSemiCategoryLogo c}}
              >
                <img src={{c.image}} class="waze-semi-category-image" alt="" />
              </div>
              <div class="waze-semi-category-icon-wrapper">
                {{dIcon "collapse" class="waze-semi-category-icon"}}
              </div>
              <wz-h7>{{c.name}}</wz-h7>
            </div>
          {{/each}}
        </div>
        <wz-dialog
          {{didInsert this.registerDialog}}
          class="wz-dialog waze-semi-category-dialog"
          size="lg"
        >
          <wz-dialog-header>{{this.dialogSemiCategory.name}}</wz-dialog-header>
          <wz-dialog-content>
            <WazeSemiCategoryDialogContent
              @semiCategory={{this.dialogSemiCategory}}
            />
          </wz-dialog-content>
          <wz-dialog-controls>
            <wz-button color="secondary" {{on "click" this.onDialogClose}}>
              {{i18n "waze.semi_categories.list.close"}}
            </wz-button>
          </wz-dialog-controls>
        </wz-dialog>
      </div>
    {{/if}}
  </template>
}
